import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowRight, Calendar, ExternalLink } from 'lucide-react';
import { createPageUrl } from '@/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslation } from '@/components/utils/translations';

const getBandColor = (band) => {
  if (!band) return 'text-gray-500';
  const normalized = String(band).replace(/\s/g, '');
  if (normalized.includes('<12')) return 'text-green-600';
  if (normalized.includes('12-24')) return 'text-yellow-600';
  if (normalized.includes('24+') || normalized.includes('>24')) return 'text-blue-600';
  return 'text-gray-500';
};

const formatBand = (band) => {
  if (!band || band === 'N/A') return null;
  const formatMap = {
    '<12 months': '<12 months', '<12m': '<12 months',
    '12-24 months': '12-24 months', '12-24m': '12-24 months',
    '>24 months': '>24 months', '24+ months': '>24 months',
    '24+m': '>24 months', '>24m': '>24 months',
  };
  return formatMap[band] || null;
};

/** The decision date itself — a point, never a span. Null when there is no date to show. */
const formatDecisionDate = (raw) => {
  if (!raw) return null;
  const dt = new Date(raw);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

/**
 * Is this date RECORDED or MODELLED? It is the difference between a contract a rep can quote and a
 * roll from a median term, and the tile said neither until now.
 */
const provenanceOf = (account) => {
  const src = String(account?.decision_window_source || '');
  if (src.includes('inst_vendor_stack')) return 'published';
  if (src.includes('closed_deals')) {
    const c = String(account?.expiry_confidence || '').toUpperCase();
    return c ? `predicted (${c})` : 'predicted';
  }
  return null;
};

/** `evidence_urls` arrives as a text[], a JSON-encoded array, or a bare string. Take the first URL. */
const firstUrl = (raw) => {
  if (!raw) return null;
  if (Array.isArray(raw)) return firstUrl(raw[0]);
  const s = String(raw).trim();
  if (!s) return null;
  if (s.startsWith('[')) {
    try { return firstUrl(JSON.parse(s)); } catch { /* fall through to the regex below */ }
  }
  const m = s.match(/https?:\/\/[^\s"'|\]},]+/);
  return m ? m[0] : null;
};

const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? (import.meta.env.VITE_API_BASE_URL || 'https://api.neogtm.com')
  : 'https://api.neogtm.com';

const dayOf = (v) => (v ? String(v).slice(0, 10) : '');

/**
 * [2026-11-01] Tiago: the renewal date should open NEO's own deal page, not the register. The tile
 * only carries the date, vendor and evidence URL, so find the closed deal behind it: same evidence
 * URL first, else same predicted expiry day from the same vendor. No match → the old external link.
 */
function useRenewalDealId(account, url) {
  const [dealId, setDealId] = useState(null);
  const tenantId = account?.tenant_id;
  const poolId = account?.global_pool_account_id;
  const day = dayOf(account?.decision_window_date);
  const vendor = String(account?.decision_window_vendor || '').trim().toLowerCase().split(/[\s,(]+/)[0];
  useEffect(() => {
    let cancelled = false;
    setDealId(null);
    if (!tenantId || !poolId || (!day && !url)) return undefined;
    (async () => {
      try {
        const qs = new URLSearchParams({ table: 'closed', accountIds: poolId, limit: '200' });
        const res = await fetch(`${API_BASE_URL}/api/procurement/query?${qs}`, {
          headers: { 'x-tenant-id': tenantId },
          credentials: 'include',
        });
        if (!res.ok) return;
        const rows = (await res.json())?.data || [];
        const byUrl = url ? rows.find((r) => r.source_url === url || r.contract_link === url) : null;
        const byDay = rows.find((r) => day && dayOf(r.predicted_expiry) === day
          && (!vendor || String(r.supplier || '').toLowerCase().includes(vendor)));
        const hit = byUrl || byDay;
        if (hit?.deal_id && !cancelled) setDealId(String(hit.deal_id));
      } catch {
        /* keep the external link */
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId, poolId, day, url, vendor]);
  return dealId;
}

export default function DecisionWindow({ account }) {
  const { t } = useTranslation();
  const date = formatDecisionDate(account?.decision_window_date);
  const band = formatBand(account?.decision_window_band);
  const color = getBandColor(account?.decision_window_band);
  const provenance = provenanceOf(account);
  const vendor = account?.decision_window_vendor || null;
  const url = firstUrl(account?.decision_window_urls);
  const dealId = useRenewalDealId(account, url);

  // The DATE is the headline when we have one. With neither, say nothing rather than "N/A",
  // which reads like a measured absence.
  //
  // [2026-10-04] THE BAND IS NOT SHOWN BESIDE THE DATE. Tiago, on the account page: "this is not
  // needed right underneath the contract end date ... <12 months". He is right — "<12 months" is
  // derived FROM the date directly above it, so it restates the headline and tells a rep nothing
  // they cannot read off "31 Mar 2027". It stays as the fallback headline for the rows that carry
  // a band and no date, which is the only place it still carries information.
  const headline = date || band || '—';
  const context = [vendor, provenance].filter(Boolean).join(' · ');

  return (
    <Card className="border-0 shadow-md">
      <CardContent className="p-3 md:p-4 flex flex-col justify-center items-center h-full">
        {/* [2026-10-04] Tiago: "in accountfinal, lets change Decision Window -> Renewal Date".
            Scoped to this tile deliberately — the Accounts table column and the Market radar keep
            "Decision Window", because there the value IS a band and a date would be a lie. */}
        <p className="text-xs md:text-sm text-[#5C6B7A] text-center">{t('renewalDate') || 'Renewal Date'}</p>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-center gap-1 my-1 cursor-help">
                <Calendar className="w-4 h-4 text-[#00C2C2]" />
                {dealId ? (
                  <Link
                    to={createPageUrl(`Procurement?deal=${encodeURIComponent(dealId)}`)}
                    onClick={(e) => e.stopPropagation()}
                    className={`text-lg md:text-2xl font-bold ${color} text-center hover:underline inline-flex items-center gap-1`}
                  >
                    {headline}
                    <ArrowRight className="w-3 h-3 opacity-60" aria-hidden />
                  </Link>
                ) : url ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className={`text-lg md:text-2xl font-bold ${color} text-center hover:underline inline-flex items-center gap-1`}
                  >
                    {headline}
                    <ExternalLink className="w-3 h-3 opacity-60" />
                  </a>
                ) : (
                  <p className={`text-lg md:text-2xl font-bold ${color} text-center`}>{headline}</p>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs bg-[#0A1A2F] text-white border-0 shadow-lg">
              <div className="space-y-2">
                {/* The tile is "Renewal Date" now, so its own tooltip header cannot still say
                    "Decision Window Monitor" — same card, two names. */}
                <p className="text-sm font-semibold text-[#00C2C2]">{t('renewalDate') || 'Renewal Date'}</p>
                <p className="text-sm">
                  {provenance === 'published'
                    ? 'A published contract end date, taken from the incumbent record. This is the date on file, not an estimate.'
                    : provenance
                      ? 'Predicted from a closed award record and shown only at medium or high confidence. Dates modelled from a typical contract length are not shown here at all.'
                      : (t('decisionWindowDesc') || 'When a contract is likely up for review, from procurement records and renewal notices.')}
                </p>
                {url && <p className="text-xs opacity-80">Source: {url}</p>}
                {account?.decision_window_rationale && (
                  <>
                    <p className="text-sm font-semibold text-[#00C2C2] mt-2">{t('decisionWindowRationale')}</p>
                    <p className="text-xs">{account.decision_window_rationale}</p>
                  </>
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <p className="text-[10px] md:text-xs text-[#5C6B7A] text-center min-h-[16px] md:min-h-[20px]">
          {context}
        </p>
      </CardContent>
    </Card>
  );
}
